import { useQuery } from "@tanstack/react-query";

import { bi } from "@/api/bi";
import { Card, CardBody, EmptyState } from "@/components/ui";

export function AdminDashboardPage() {
  const ov = useQuery({ queryKey: ["bi-overview"], queryFn: () => bi.overview() });
  const vol = useQuery({ queryKey: ["bi-volume"], queryFn: () => bi.volume(30) });
  const venues = useQuery({ queryKey: ["bi-venues"], queryFn: () => bi.venues(30) });

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-base font-semibold text-term-fg">Dashboard</h1>
        <p className="text-2xs text-term-mute">
          AUM, volume, and venue scorecard. Read-only snapshot of org KPIs.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard label="Customers" value={ov.data?.customers ?? "—"} />
        <KpiCard label="Fills (24h)" value={ov.data?.fills_24h ?? "—"} />
        <KpiCard
          label="AUM"
          value={
            ov.data
              ? Object.entries(ov.data.aum_per_currency)
                  .map(([c, v]) => `${c} ${v.toLocaleString()}`)
                  .join(" · ") || "—"
              : "—"
          }
        />
      </div>

      <Card>
        <CardBody className="p-0">
          <div className="border-b border-term-border bg-term-elev px-3 py-1.5">
            <p className="text-2xs font-semibold uppercase text-term-mute">Daily volume — last 30d</p>
          </div>
          {vol.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !vol.data || vol.data.length === 0 ? (
            <EmptyState title="No filled orders yet" />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Day</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Orders</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Notional</th>
                </tr>
              </thead>
              <tbody>
                {vol.data.map((r) => (
                  <tr key={r.day} className="border-t border-term-border">
                    <td className="px-3 py-1.5 text-term-dim">
                      {new Date(r.day).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-1.5 text-right text-term-fg">
                      {r.orders.toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 text-right text-term-fg">
                      {r.notional.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          <div className="border-b border-term-border bg-term-elev px-3 py-1.5">
            <p className="text-2xs font-semibold uppercase text-term-mute">Venue scorecard — last 30d</p>
          </div>
          {venues.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !venues.data || venues.data.length === 0 ? (
            <EmptyState title="No execution-quality data yet" />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Venue</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Fills</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Avg latency (ms)</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Avg slippage (bps)</th>
                </tr>
              </thead>
              <tbody>
                {venues.data.map((r) => (
                  <tr key={r.venue} className="border-t border-term-border">
                    <td className="px-3 py-1.5 text-term-fg">{r.venue}</td>
                    <td className="px-3 py-1.5 text-right text-term-dim">
                      {r.fills.toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 text-right text-term-dim">
                      {r.avg_latency_ms !== null ? r.avg_latency_ms.toFixed(0) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right text-term-dim">
                      {r.avg_slippage_bps !== null ? r.avg_slippage_bps.toFixed(1) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardBody>
        <p className="text-2xs uppercase tracking-wider text-term-mute">{label}</p>
        <p className="mt-1 text-xl font-semibold text-term-fg">{value}</p>
      </CardBody>
    </Card>
  );
}
